import Popup from "@/components/Popup/Popup";
import { ALL_PERMISSIONS, PERMISSION_LABELS, permissionsFromBitmask } from "@/utils/constants/permission";
import { userConfigClipboardLocalStorage } from "@/utils/constants/localStorage";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Button, Checkbox, Chip, FormControl, InputLabel, ListItemText, MenuItem, OutlinedInput, Select, Stack, TextField, Typography } from "@mui/material";
import { ChangeEvent, FC, useEffect, useRef, useState } from "react";

interface Props {
  open: boolean;
  users: User[];
  onClose: () => void;
  title?: string;
  disablePermissions?: boolean;

  // eslint-disable-next-line no-unused-vars
  onSave: (username: string, config: UpdateUserConfigDTO) => Promise<void>;
  // eslint-disable-next-line no-unused-vars
  onSavePermissions: (username: string, permissions: FeaturePermission[]) => Promise<void>;
}

const EditUserConfigPopup: FC<Props> = ({ open, users, onClose, onSave, onSavePermissions, title, disablePermissions }) => {
  const [grammarFileName, setGrammarFileName] = useState<string | null>(null);
  const [grammarContent, setGrammarContent] = useState<string | null>(null);
  const [ledMatrixFileName, setLedMatrixFileName] = useState<string | null>(null);
  const [ledMatrixContent, setLedMatrixContent] = useState<string | null>(null);
  const [maskMac, setMaskMac] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState<FeaturePermission[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasClipboard, setHasClipboard] = useState(false);
  const isUser = users.length === 1 && users[0].role === "USER";
  const isSup = users.length === 1 && users[0].role === "SUPERVISOR";

  useEffect(() => {
    setHasClipboard(userConfigClipboardLocalStorage.get() !== null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (users.length === 1) {
      const [user] = users;
      setGrammarFileName(user.userConfig?.grammarFileName ?? null);
      setGrammarContent(user.userConfig?.grammarContent ?? null);
      setLedMatrixFileName(user.userConfig?.ledMatrixFileName ?? null);
      setLedMatrixContent(user.userConfig?.ledMatrixContent ?? null);
      setMaskMac(user.userConfig?.maskMac ?? "");
      setSelectedPermissions(permissionsFromBitmask(user.featurePermissions ?? 0));
    } else {
      setGrammarFileName(null);
      setGrammarContent(null);
      setLedMatrixFileName(null);
      setLedMatrixContent(null);
      setMaskMac("");
      setSelectedPermissions([]);
    }
  }, [open]);

  const grammarInputRef = useRef<HTMLInputElement>(null);
  const ledMatrixInputRef = useRef<HTMLInputElement>(null);

  const handleGrammarFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setGrammarFileName(file.name);
      setGrammarContent(ev.target?.result as string);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleLedMatrixFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setLedMatrixFileName(file.name);
      setLedMatrixContent(ev.target?.result as string);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleSubmit = async () => {
    if (users.length === 0) return;
    setLoading(true);
    const config: UpdateUserConfigDTO = {
      grammarFileName,
      grammarContent,
      ledMatrixFileName,
      ledMatrixContent,
      maskMac: maskMac.trim() || null,
    };
    try {
      const saves: Promise<void>[] = users.map((u) => onSave(u.alias ?? u.username, config));
      if (!disablePermissions) {
        saves.push(...users.map((u) => onSavePermissions(u.alias ?? u.username, selectedPermissions)));
      }
      await Promise.all(saves);
      handleClose();
    } catch {
      // error handled by parent
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    userConfigClipboardLocalStorage.set({ grammarFileName, grammarContent, ledMatrixFileName, ledMatrixContent, maskMac: maskMac.trim() || null });
    setHasClipboard(true);
  };

  const handlePaste = () => {
    const config = userConfigClipboardLocalStorage.get();
    if (!config) return;
    setGrammarFileName(config.grammarFileName);
    setGrammarContent(config.grammarContent);
    setLedMatrixFileName(config.ledMatrixFileName);
    setLedMatrixContent(config.ledMatrixContent);
    setMaskMac(config.maskMac ?? "");
  };

  const handleClose = () => {
    setGrammarFileName(null);
    setGrammarContent(null);
    setLedMatrixFileName(null);
    setLedMatrixContent(null);
    setMaskMac("");
    setSelectedPermissions([]);
    onClose();
  };

  return (
    <Popup
      open={open}
      onClose={handleClose}
      title={title ?? (users.length === 1 ? `Configuration de ${users[0].alias ?? users[0].username}` : "Configuration")}
      modalSize={{ width: 480, height: "auto" }}
      actions={
        <Stack direction="row" spacing={1} justifyContent="space-between" width="100%">
          <Stack direction="row" spacing={1}>
            <Button size="small" startIcon={<ContentCopyIcon />} onClick={handleCopy}>
              Copier
            </Button>
            <Button size="small" startIcon={<ContentPasteIcon />} onClick={handlePaste} disabled={!hasClipboard}>
              Coller
            </Button>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button onClick={handleClose}>Annuler</Button>
            <Button variant="contained" onClick={handleSubmit} disabled={loading}>
              Enregistrer
            </Button>
          </Stack>
        </Stack>
      }>
      
      <Stack spacing={2}>
        <Stack spacing={1}>
          {isUser && (
            <Typography variant="body2" color="text.secondary">
              Grammaire (JSON)
            </Typography>
          )}
          <Stack direction="row" alignItems="center" spacing={1}>
            {isUser &&(
              <Button
                variant="outlined"
                size="small"
                startIcon={<UploadFileIcon />}
                onClick={() => grammarInputRef.current?.click()}>
                Importer
              </Button>
            )}
            {isUser &&(
              <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                {grammarFileName ?? "Aucun fichier"}
              </Typography>
            )}
          </Stack>
          {isUser &&(
            <input
              ref={grammarInputRef}
              type="file"
              accept=".json,application/json"
              style={{ display: "none" }}
              onChange={handleGrammarFile}
            />
          )}
        </Stack>

        <Stack spacing={1}>
          {isUser &&(
            <Typography variant="body2" color="text.secondary">
              Matrice LED (CSV)
            </Typography>
          )}
          <Stack direction="row" alignItems="center" spacing={1}>
            {isUser &&(
              <Button
                variant="outlined"
                size="small"
                startIcon={<UploadFileIcon />}
                onClick={() => ledMatrixInputRef.current?.click()}>
                Importer
              </Button>
            )}
            {isUser &&(
              <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                {ledMatrixFileName ?? "Aucun fichier"}
              </Typography>
            )}
          </Stack>
          {isUser &&(
            <input
              ref={ledMatrixInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={handleLedMatrixFile}
            />
          )}
        </Stack>

        {isUser &&(
          <TextField
            label="Adresse MAC du masque"
            fullWidth
            size="small"
            value={maskMac}
            onChange={(e) => setMaskMac(e.target.value)}
            placeholder="XX:XX:XX:XX:XX:XX"
          />
        )}

        {(!disablePermissions && isSup) && (
          <FormControl fullWidth size="small">
            <InputLabel>Permissions</InputLabel>
            <Select
              multiple
              value={selectedPermissions}
              onChange={(e) => setSelectedPermissions(e.target.value as FeaturePermission[])}
              input={<OutlinedInput label="Permissions" />}
              renderValue={(selected) => (
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {selected.map((p) => (
                    <Chip
                      key={p}
                      label={PERMISSION_LABELS[p]}
                      size="small"
                      color="primary"
                      onMouseDown={(e) => e.stopPropagation()}
                      onDelete={() => setSelectedPermissions((prev) => prev.filter((x) => x !== p))}
                    />
                  ))}
                </Stack>
              )}>
              {ALL_PERMISSIONS.map((permission) => (
                <MenuItem key={permission} value={permission}>
                  <Checkbox checked={selectedPermissions.includes(permission)} />
                  <ListItemText primary={PERMISSION_LABELS[permission]} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Stack>
    </Popup>
  );
};

export default EditUserConfigPopup;
